import { UzButton, UzDatePicker, UzDialog, UzInput, UzMoneyInput, UzSelect } from '@utazzo/ui';
import React, { useEffect, useState } from 'react';

import { formatInrPaise } from '../invoices.helpers';
import { useBankAccountOptions } from '../use-invoices';
import { useReceiveSupplierRefund } from '../use-supplier-refunds';

import { rupeesToPaise, toIsoDate } from './invoice-form.helpers';

import type { SupplierRefundRow } from '@utazzo/shared';

interface ReceiveRefundDialogProps {
  refund: SupplierRefundRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Marks a PENDING supplier refund as received: date (DD-MM-YYYY picker),
 * amount, mode, reference, optional bank account and note.
 */
export const ReceiveRefundDialog = ({
  refund,
  open,
  onOpenChange,
}: ReceiveRefundDialogProps): React.JSX.Element => {
  const receive = useReceiveSupplierRefund();
  const bankOptions = useBankAccountOptions();

  const [amount, setAmount] = useState<number | ''>('');
  const [receivedAt, setReceivedAt] = useState<Date | null>(new Date());
  const [mode, setMode] = useState('');
  const [reference, setReference] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open || !refund) return;
    setAmount(Number(refund.amountPaise) / 100);
    setReceivedAt(new Date());
    setMode('');
    setReference('');
    setBankAccountId('');
    setNote('');
  }, [open, refund]);

  const amountValid = typeof amount === 'number' && amount > 0;
  const canSubmit = !!refund && amountValid && !!receivedAt && !receive.isPending;

  const handleSubmit = (): void => {
    if (!refund || !amountValid || !receivedAt) return;
    receive.mutate(
      {
        id: refund.id,
        payload: {
          receivedAmountPaise: rupeesToPaise(amount),
          receivedAt: toIsoDate(receivedAt) as string,
          ...(mode.trim() ? { receivedMode: mode.trim() } : {}),
          ...(reference.trim() ? { receivedReference: reference.trim() } : {}),
          ...(bankAccountId ? { bankAccountId } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <UzDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Mark refund received"
      description={
        refund
          ? `${refund.supplierName ?? 'Supplier'} owes ${formatInrPaise(refund.amountPaise)}${refund.invoiceNumber ? ` on ${refund.invoiceNumber}` : ''}.`
          : undefined
      }
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </UzButton>
          <UzButton onClick={handleSubmit} disabled={!canSubmit} loading={receive.isPending}>
            Mark received
          </UzButton>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <UzMoneyInput
          label="Received amount"
          required
          value={amount}
          onValueChange={(v) => setAmount(v ?? '')}
        />
        <UzDatePicker
          label="Received date"
          required
          value={receivedAt}
          onValueChange={setReceivedAt}
        />
        <UzInput label="Mode" value={mode} onChange={(e) => setMode(e.target.value)} />
        <UzInput
          label="Reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
        <UzSelect
          label="Bank account (optional)"
          placeholder="Select bank account"
          value={bankAccountId}
          onValueChange={setBankAccountId}
          options={(bankOptions.data ?? []).map((b) => ({
            value: b.id,
            label: `${b.label} - ${b.bankName} (${b.accountNumber})`,
          }))}
          containerClassName="sm:col-span-2"
        />
        <UzInput
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          containerClassName="sm:col-span-2"
        />
      </div>
    </UzDialog>
  );
};
